import { CSSResultGroup, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';

import {
  OutlineElement,
  watch,
  emit,
  defaultIconConfig,
} from '@phase2/outline-core';
import { getIconLibrary, unwatchIcon, watchIcon } from './library';
import { requestIcon } from './request';
import componentStyles from './outline-icon-baseline.css.lit';

const parser = new DOMParser();

/**
 * @since 1.0
 * @status stable
 *
 * @event outline-load - Emitted when the icon has loaded.
 * @event outline-error - Emitted when the icon fails to load due to an error.
 *
 * @csspart base - The component's base wrapper.
 */
@customElement('outline-icon-baseline')
export class OutlineIconBaseline extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  @state() private svg = '';

  /** The name of the icon to draw. */
  @property({ type: String, reflect: true, attribute: 'name' })
  name?: string;

  /**
   * An external URL of an SVG file.
   *
   * WARNING: Be sure you trust the content you are including as it will be executed as code and can result in XSS attacks.
   */
  @property({ type: String, attribute: 'src' })
  src?: string;

  /**
   * If this is excluded, it suggests that the icon serves a purely decorative purpose.
   * If the intention is for the icon to provide information, ensure that the label
   * attribute is included to be accessible by assistive devices.
   */
  @property({ attribute: 'label' })
  label: string | boolean = false;

  /** The name of a registered custom icon library. */
  @property({ type: String, attribute: 'library' })
  library = 'system';

  /**
   * The width and height of the icon.
   * @param size in pixels
   * @default 32px
   */
  @property({ attribute: 'size' })
  size: string | boolean = defaultIconConfig.defaults.size;

  connectedCallback() {
    super.connectedCallback();
    watchIcon(this);
  }

  firstUpdated() {
    this.setIcon();
    this.sizeIcon();
  }

  updated() {
    this.setIcon();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    unwatchIcon(this);
  }

  private sizeIcon() {
    if (
      this.size &&
      typeof this.size === 'string' &&
      this.size !== defaultIconConfig.defaults.size
    ) {
      const icon: HTMLElement | null | undefined =
        this.shadowRoot?.querySelector('.icon');
      icon?.style.setProperty('width', this.size);
      icon?.style.setProperty('height', this.size);
    }
  }

  private getUrl() {
    const library = getIconLibrary(this.library);
    if (this.name && library) {
      return library.resolver(this.name);
    }
    return this.src;
  }

  /** @internal Fetches the icon and redraws it. Used to handle library registrations. */
  redraw() {
    this.setIcon();
  }

  @watch('name')
  @watch('src')
  @watch('library')
  async setIcon() {
    const library = getIconLibrary(this.library);
    const url = this.getUrl();
    if (url) {
      try {
        const file = await requestIcon(url);
        if (url !== this.getUrl()) {
          // If the url has changed while fetching the icon, ignore this request
          return;
        } else if (file.ok) {
          const doc = parser.parseFromString(file.svg, 'text/html');
          const svgEl = doc.body.querySelector('svg');

          if (svgEl !== null) {
            library?.mutator?.(svgEl);

            this.svg = svgEl.outerHTML;
            emit(this, 'outline-load');
          } else {
            this.svg = '';
            emit(this, 'outline-error', { detail: { status: file.status } });
          }
        } else {
          this.svg = '';
          emit(this, 'outline-error', { detail: { status: file.status } });
        }
      } catch {
        emit(this, 'outline-error', { detail: { status: -1 } });
      }
    } else if (this.svg.length > 0) {
      // If we can't resolve a URL and an icon was previously set, remove it
      this.svg = '';
    }
  }

  render() {
    const hasLabel = typeof this.label === 'string' && this.label.length > 0;

    const wrapperClasses = {
      icon: true,
      [`icon-library--${this.library}`]: this.library,
    };

    return html` <div
      part="base"
      class="${classMap(wrapperClasses)}"
      role=${ifDefined(hasLabel ? 'img' : undefined)}
      aria-label=${ifDefined(hasLabel ? this.label : undefined)}
    >
      ${unsafeSVG(this.svg)}
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'outline-icon-baseline': OutlineIconBaseline;
  }
}
